import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { RestService } from '../../../shared/services/rest.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { UserRepository } from '../../user/states/user.repository';
import {
  AnimalNoteExportModel,
  AnimalNoteExportPayload,
  AnimalNoteModel
} from '../models/animal-note.model';

@Injectable({ providedIn: 'root' })
export class AnimalNoteRepository {
  constructor(
    private readonly _restService: RestService,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService,
    private readonly _userRepository: UserRepository
  ) {}

  getByAnimalId(animalId: number): Observable<AnimalNoteModel[]> {
    return this._restService
      .httpGet(`/animal_notes?pagination=false&animal.id=${animalId}`)
      .pipe(
        take(1),
        catchError(() => {
          this._toast.showError(this._translateService.translate('animalNotes.toast.loadError'));
          return of([]);
        })
      );
  }

  create(animalId: number, title: string, description: string): Observable<AnimalNoteModel | null> {
    return this._userRepository.getActiveUser().pipe(
      take(1),
      switchMap((activeUser) => {
        const creatorId = activeUser?.id;
        if (!creatorId) {
          this._toast.showError(this._translateService.translate('animalNotes.toast.saveError'));
          return of(null);
        }

        const payload = {
          animal: { id: animalId },
          creator: { id: creatorId },
          title,
          description
        };

        return this._restService.httpPost('/animal_notes', payload).pipe(take(1));
      }),
      catchError(() => {
        this._toast.showError(this._translateService.translate('animalNotes.toast.saveError'));
        return of(null);
      })
    );
  }

  update(noteId: number, title: string, description: string): Observable<AnimalNoteModel | null> {
    return this._restService.httpPut(`/animal_notes/${noteId}`, { title, description }).pipe(
      take(1),
      catchError(() => {
        this._toast.showError(this._translateService.translate('animalNotes.toast.saveError'));
        return of(null);
      })
    );
  }

  delete(noteId: number): Observable<boolean> {
    return this._restService.httpDelete(`/animal_notes/${noteId}`).pipe(
      take(1),
      map(() => true),
      catchError(() => {
        this._toast.showError(this._translateService.translate('animalNotes.toast.deleteError'));
        return of(false);
      })
    );
  }

  getExportsByAnimalId(animalId: number): Observable<AnimalNoteExportModel[]> {
    return this._restService
      .httpGet(`/animal_note_exports?animal.id=${animalId}`)
      .pipe(
        take(1),
        catchError(() => {
          this._toast.showError(this._translateService.translate('animalNotes.toast.exportLoadError'));
          return of([]);
        })
      );
  }

  exportPdf(animalId: number, payload: AnimalNoteExportPayload): Observable<Blob | null> {
    return this._restService
      .httpPost(`/animals/${animalId}/notes/export_pdf`, payload, { responseType: 'blob' })
      .pipe(
        take(1),
        catchError(() => {
          this._toast.showError(this._translateService.translate('animalNotes.toast.exportError'));
          return of(null);
        })
      );
  }
}

